import { DefaultButton, IconButton, Panel, PanelType, Separator, Stack, Text, TooltipHost, useTheme } from '@fluentui/react';
import { IGroupConfigurationComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import { Delay, ScrollToPositionCascaded } from 'global/Utility/TSFunctions';
import React, { useEffect, useMemo, useState } from 'react';
import { ICompareConfiguration } from '../CompareLookupDialog/CompareLookupDialog';
import {
    IMarkerSet,
    QUICK_NAV_EMBEDDED_PANEL_HIDDEN,
    QUICK_NAV_EMBEDDED_PANEL_VISIBLE,
    QUICK_NAV_OVERLAY_CONTENT_STYLE,
    QUICK_NAV_STACK_TOKENS,
} from './CompareQuickNavOverlay.styles';
import { buildQuickNavCategoryPanel } from './CompareQuickNavOverlay.tree';
import QuickNavStickySearchBar from './QuickNavStickySearchBar';

interface ICompareQuickNavOverlayProps {
    model: IGroupConfigurationComparisonVM;
    isHidden: boolean;
    markers: IMarkerSet;
    configuration: ICompareConfiguration;
    onDismiss?: () => void;
    embedded?: boolean;
    onTogglePinned?: () => void;
}

const CompareQuickNav: React.FC<ICompareQuickNavOverlayProps> = (props) => {
    const {
        model,
        isHidden,
        markers,
        configuration,
        onDismiss,
        embedded = false,
        onTogglePinned,
    } = props;

    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const [showDifferencesOnlyLocal, setShowDifferencesOnlyLocal] = useState(false);
    const [mountOnce, setMountOnce] = useState(false);
    const theme = useTheme();
    const effectiveShowDifferencesOnly = configuration.showDifferencesOnly || showDifferencesOnlyLocal;
    const quickNavBackgroundColor = theme.semanticColors.bodyBackground;
    const quickNavBorderColor = theme.semanticColors.bodyDivider;

    useEffect(() => {
        // Lazily mount expensive quick-nav tree only when shown once.
        if (!isHidden && !mountOnce) {
            setMountOnce(true);
        }
    }, [isHidden, mountOnce]);

    const matchesSearch = useMemo(() => {
        const cache = new Map<string | null | undefined, boolean>();
        const searchQueryLowerCase = searchQuery?.toLowerCase();

        return (value?: string | null) => {
            if (!searchQueryLowerCase) {
                return true;
            }

            if (cache.has(value)) {
                return cache.get(value)!;
            }

            const result = value?.toLowerCase().includes(searchQueryLowerCase) ?? false;
            cache.set(value, result);
            return result;
        };
    }, [searchQuery]);

    const onNavigateAsync = async (links: string[]) => {
        if (!embedded) {
            onDismiss?.();
        }

        await Delay(300);
        await ScrollToPositionCascaded(links);
    };

    const onRenderHeader = () => (
        <Stack
            horizontal
            verticalAlign="center"
            horizontalAlign="space-between"
            styles={{ root: { width: "100%", paddingRight: "8px" } }}
        >
            <Text variant="xLarge" styles={{ root: { paddingLeft: '25px', paddingBottom: '8px' } }}>Quick Navigation</Text>
            <TooltipHost content={embedded ? "Unpin Quick Nav" : "Pin Quick Nav"}>
                {onTogglePinned && (
                    <IconButton
                        onClick={onTogglePinned}
                        iconProps={{ iconName: embedded ? "Pinned" : "Pin" }}
                        ariaLabel={embedded ? "Unpin Quick Nav" : "Pin Quick Nav"}
                    />
                )}
            </TooltipHost>
        </Stack>
    );

    const panelNodes = useMemo(() => {
        if (!mountOnce || !model) {
            return null;
        }

        const ctx = {
            markers,
            matchesSearch,
            showDifferencesOnly: effectiveShowDifferencesOnly,
            backgroundColor: quickNavBackgroundColor,
            onNavigateAsync: onNavigateAsync,
        };

        return [
            model.groupConfiguration,
            model.nonStandardTemplate,
            model.plan,
            model.benefit,
            model.accountsReceivable,
            model.groupNumber,
            model.cesNumber,
            model.department,
            model.feesAndPremium,
            model.feesSummary,
            model.stopLossSummary,
            model.stopLoss,
        ]
            .map((list) => buildQuickNavCategoryPanel(list, ctx))
            .filter((node): node is React.ReactNode => node !== null);
    }, [
        mountOnce,
        model,
        effectiveShowDifferencesOnly,
        matchesSearch,
        markers,
        quickNavBackgroundColor,
    ]);

    if (embedded) {
        return (
            <div
                style={{
                    ...(QUICK_NAV_EMBEDDED_PANEL_VISIBLE),
                    backgroundColor: quickNavBackgroundColor,
                    borderRight: `1px solid ${quickNavBorderColor}`,
                }}
            >
                <div
                    style={{
                        ...QUICK_NAV_OVERLAY_CONTENT_STYLE,
                        marginLeft: '16px',
                        width: 'calc(100% - 24px)',
                    }}
                >
                    <Stack tokens={QUICK_NAV_STACK_TOKENS}>
                        {onRenderHeader()}
                        <QuickNavStickySearchBar
                            isHidden={isHidden}
                            backgroundColor={quickNavBackgroundColor}
                            showDifferencesOnly={effectiveShowDifferencesOnly}
                            showDifferencesOnlyDisabled={configuration.showDifferencesOnly}
                            onSearch={setSearchQuery}
                            onShowDifferencesOnlyChange={setShowDifferencesOnlyLocal}
                        />
                        {panelNodes}
                    </Stack>
                </div>
            </div>
        );
    }

    return (
        <Panel
            isOpen={!isHidden}
            onDismiss={onDismiss}
            type={PanelType.customNear}
            customWidth="max(420px, 40vw)"
            closeButtonAriaLabel="Close"
            onRenderHeader={onRenderHeader}
            isFooterAtBottom={true}
            styles={{ footer: { position: 'relative', zIndex: 1100 } }}
            onRenderFooterContent={() => (
                <DefaultButton text="Close" onClick={onDismiss} />
            )}
        >
            <Separator />
            <Stack tokens={QUICK_NAV_STACK_TOKENS}>
                <QuickNavStickySearchBar
                    isHidden={isHidden}
                    backgroundColor={quickNavBackgroundColor}
                    showDifferencesOnly={effectiveShowDifferencesOnly}
                    showDifferencesOnlyDisabled={configuration.showDifferencesOnly}
                    onSearch={setSearchQuery}
                    onShowDifferencesOnlyChange={setShowDifferencesOnlyLocal}
                />
                {panelNodes}
            </Stack>
        </Panel>
    );
};

export default CompareQuickNav;

